import styled, {css} from "styled-components";
import {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react";
import {colorPalette} from "../styles/colorPalette";
import CloseIcon from "../icons/xmark.svg?react";
import {baseDistance, fontSize, fontWeight, radius} from "../styles/styles";
import {IconButtonV5} from "../interaction/IconButtonV5";
import {XyiconFeature} from "../../../generated/api/base";
import type {IViewSort, ViewChangeType} from "../../../data/models/ViewUtils";
import type {ISpotlightRule} from "../../../data/models/View";
import {useAppStore} from "../../../StateManager";
import ChevronDownDouble from "../icons/chevron-down-double.svg?react";
import {ToggleSwitchFieldStyled, ToggleSwitchFieldV5} from "../details/ToggleSwitchFieldV5";
import {ButtonV5} from "../button/ButtonV5";
import ResetIcon from "../icons/refresh-ccw.svg?react";
import type {SpaceViewRenderer} from "../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import type {IFilterState} from "../../../data/models/filter/Filter";
import {ObjectUtils} from "../../../utils/data/ObjectUtils";
import {SaveToViewButtonV5} from "../abstract/view/SaveToViewButtonV5";
import {HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import {useClickOutside} from "../utils";
import {PopupBodyStyled, PopupHeaderStyled, PopupV5} from "./PopupV5";

export interface IAlertProps {
	readonly feature?: XyiconFeature;
	readonly manageColumns?: boolean;
	readonly type: ViewChangeType[];
	readonly spaceViewRenderer?: SpaceViewRenderer;
	readonly isSideGrid?: boolean;
	readonly onClose?: () => void;
	readonly isDetailsPanelOpen?: boolean;
	readonly dropdownStyle?: boolean;
	readonly divRef: React.RefObject<HTMLDivElement>;
}

// module-level storage (like static in class)
let alertInstances: React.Dispatch<React.SetStateAction<boolean>>[] = [];
const MAX_OPEN_INSTANCES = 1;

export const AlertV5 = observer((props: IAlertProps) => {
	const typeLabels: Record<string, string> = {
		filters: "Filters",
		captions: "Labels",
		columns: "Columns",
		spotlight: "Spotlight",
		layers: "Layers",
	};
	const appState = useAppStore((state) => state.appState);

	const {feature, type, spaceViewRenderer, isSideGrid, isDetailsPanelOpen, dropdownStyle, divRef} = props;
	const selectedView = appState.actions.getSelectedView(isSideGrid ? XyiconFeature.SpaceEditor : feature);

	const viewKey = `${feature}-${selectedView?.id ?? "unknown"}`;
	const currentCount = Number(sessionStorage.getItem(viewKey) || 0);
	sessionStorage.setItem(viewKey, String(currentCount + 1));
	const [isOpen, setIsOpen] = useState(() => currentCount === 0);

	const [unsavedChanges, setUnsavedChanges] = useState<ViewChangeType[]>(type);
	const [selectedUnsavedChanges, setSelectedUnsavedChanges] = useState<ViewChangeType[]>([]);
	const prevUnsavedChangesRef = useRef<ViewChangeType[]>([]);

	const handleToggleChange = (type: ViewChangeType) => {
		setSelectedUnsavedChanges((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
	};

	const onResetClick = async () => {
		for (const type of [...unsavedChanges]) {
			switch (type) {
				case "captions": {
					selectedView.spaceEditorViewSettings.captions =
						selectedView.getSavedSpaceEditorViewSettings()?.captions || selectedView.getDefaultCaptionConfig();
					await spaceViewRenderer.boundaryManager.captionManager.updateCaptions();
					await spaceViewRenderer.xyiconManager.captionManager.updateCaptions();
					break;
				}
				case "columns":
					selectedView.setSerializedColumns(selectedView.data.columns);
					const savedColumnSorts = JSON.parse(selectedView.data.sorts);
					const sorts = savedColumnSorts;
					selectedView.setSorts(sorts as IViewSort[]);
					break;
				case "spotlight": {
					const saved = (selectedView.getSavedDataForType("spotlight", false) || {}) as {
						xyiconSpotlightRules?: ISpotlightRule[];
						boundarySpotlightRules?: ISpotlightRule[];
						listViewSpotlightRules?: ISpotlightRule[];
					};
					const restored: {
						xyiconSpotlightRules: ISpotlightRule[];
						boundarySpotlightRules: ISpotlightRule[];
						listViewSpotlightRules: ISpotlightRule[];
					} = {
						xyiconSpotlightRules: saved.xyiconSpotlightRules || [],
						boundarySpotlightRules: saved.boundarySpotlightRules || [],
						listViewSpotlightRules: saved.listViewSpotlightRules || [],
					};

					selectedView.spotlightRules = restored;

					// If we have the renderer (space editor), re-apply formatting immediately
					spaceViewRenderer?.xyiconManager?.onFormattingRulesModified();
					spaceViewRenderer?.boundaryManager?.onFormattingRulesModified();
					break;
				}
				case "filters": {
					const oldFilters = selectedView.getSavedDataForType("filters", false) as IFilterState | undefined;
					selectedView.setFilters(ObjectUtils.deepClone(oldFilters));
					break;
				}
				case "layers": {
					selectedView.spaceEditorViewSettings.layers =
						selectedView.getSavedSpaceEditorViewSettings()?.layers || selectedView.getDefaultLayerSettings();
					spaceViewRenderer.spaceItemController.onLayerSettingsModified();
					spaceViewRenderer.tileManager.container.visible = !selectedView.spaceEditorViewSettings.layers.background.isHidden;
					break;
				}
			}

			// Remove type after handling
			const index = unsavedChanges.indexOf(type);

			if (index > -1) {
				unsavedChanges.splice(index, 1);
			}
		}

		setSelectedUnsavedChanges([]);
		prevUnsavedChangesRef.current = [];
		// Reset view-specific render counter so next alert opens expanded again
		sessionStorage.setItem(viewKey, "0");
	};

	useEffect(() => {
		const prev = prevUnsavedChangesRef.current;
		const now = type;

		const newOnes = now.filter((item) => !prev.includes(item));
		const removed = prev.filter((item) => !now.includes(item));

		setSelectedUnsavedChanges((prevSelected) => {
			let updated = [...prevSelected];

			if (newOnes.length > 0) {
				updated = [...updated, ...newOnes];
			}
			if (removed.length > 0) {
				updated = updated.filter((item) => !removed.includes(item));
			}
			return updated;
		});

		prevUnsavedChangesRef.current = now;
	}, [type]);

	useEffect(() => {
		setUnsavedChanges(type);
	}, [type]);

	useClickOutside([divRef], (event: MouseEvent) => {
		close();
	});

	const setIsOpenRef = useRef(setIsOpen);

	setIsOpenRef.current = setIsOpen;

	useEffect(() => {
		// add this instance
		alertInstances.push(setIsOpenRef.current);

		// close older instances if over limit
		for (let i = 0; i < alertInstances.length - MAX_OPEN_INSTANCES; i++) {
			alertInstances[i](false);
		}

		return () => {
			// remove this instance on unmount
			alertInstances = alertInstances.filter((fn) => fn !== setIsOpenRef.current);
		};
	}, []);

	useEffect(() => {
		if (isOpen) {
			// close all other instances
			for (const fn of alertInstances) {
				if (fn !== setIsOpenRef.current) {
					fn(false);
				}
			}
		}
	}, [isOpen]);

	const open = () => {
		setIsOpen(true);
		props?.onClose;
	};
	const close = () => setIsOpen(false);

	const panelWidths = useAppStore((state) => state.panelWidths);
	const panelId = feature === XyiconFeature.SpaceEditor ? "space-editor-details-panel" : "module-view-details-panel";
	const panelWidth = panelWidths[panelId] ?? 432;

	return (
		<>
			{isOpen && (
				<AlertPopupStyled
					onClose={close}
					label="Manage Columns"
					className="othe cloass"
					centerOnScreen={false}
					parentRef={divRef.current}
					noButtons={true}
					verticalAlignment={VerticalAlignment.bottomOuter}
					horizontalAlignment={HorizontalAlignment.right}
					offsetX={0}
					offsetY={feature === XyiconFeature.SpaceEditor && !dropdownStyle ? -39 : -33}
				>
					<AlertStyled
						$isSpaceEditor={feature === XyiconFeature.SpaceEditor}
						$isDetailsPanelOpen={isDetailsPanelOpen}
						onClick={() => props.onClose?.()}
					>
						<div className="yellow-strip"></div>
						<div className="body">
							<AlertTitleStyled $isOpen={isOpen}>
								Save View
								<IconButtonV5
									IconComponent={CloseIcon}
									onClick={close}
								/>
							</AlertTitleStyled>
							<AlertBodyStyled $twoCols={unsavedChanges.length > 4}>
								<AlertMessageStyled>Saved changes reflect for everyone with access</AlertMessageStyled>
								<div className="toggles">
									{unsavedChanges.map((type) => (
										<ToggleSwitchFieldV5
											key={type}
											label={typeLabels[type] ?? type}
											value={selectedUnsavedChanges.includes(type)}
											onChange={() => handleToggleChange(type)}
											labelFirst={false}
											noBooleanLabel={true}
										/>
									))}
								</div>
								<ButtonContainerStyled>
									<div>
										<ButtonV5
											label="Reset"
											IconComponent={ResetIcon}
											type="tertiary"
											disabled={false}
											onClick={onResetClick}
										/>
									</div>
									<SaveToViewButtonV5
										feature={feature}
										viewChangeType={selectedUnsavedChanges}
										disabled={selectedUnsavedChanges.length === 0}
										isSideGrid={isSideGrid}
										isOpen={true}
										hasUnsavedChanges={true}
										onSaveClicked={() => {
											sessionStorage.setItem(viewKey, "0");
										}}
									/>
								</ButtonContainerStyled>
							</AlertBodyStyled>
						</div>
					</AlertStyled>
				</AlertPopupStyled>
			)}
			<SaveViewStyled
				$isSpaceEditor={feature === XyiconFeature.SpaceEditor}
				$isDetailsPanelOpen={isDetailsPanelOpen}
				$isSideGrid={isSideGrid}
				$dropDownStyle={dropdownStyle}
				$isBoundary={feature === XyiconFeature.Boundary}
				ref={divRef}
				style={{
					visibility: isOpen ? "hidden" : "visible",
					right: isDetailsPanelOpen && !isSideGrid ? `${panelWidth + 16}px` : undefined,
				}}
				onClick={() => props.onClose?.()}
			>
				<AlertTitleStyled>
					<SaveToViewButtonV5
						feature={feature}
						viewChangeType={selectedUnsavedChanges}
						disabled={selectedUnsavedChanges.length === 0}
						isSideGrid={isSideGrid}
						isOpen={false}
						hasUnsavedChanges={true}
						onSaveClicked={() => {
							// reset counter for this view after saving
							sessionStorage.setItem(viewKey, "0");
						}}
					/>
				</AlertTitleStyled>
				<div
					className="arrow-down"
					onClick={open}
				>
					<ChevronDownDouble />
				</div>
			</SaveViewStyled>
		</>
	);
});

const AlertTitleStyled = styled.div<{$isOpen?: boolean}>`
	display: flex;
	padding: ${(props) => (props.$isOpen ? "0" : "4px")};
	align-items: center;
	justify-content: space-between;
	font-size: ${fontSize.lg};
	font-weight: ${fontWeight.bold};
	color: ${colorPalette.gray.c950};
	height: ${(props) => (props.$isOpen ? "32px" : "30px")};
	font-weight: ${fontWeight.bold};
	z-index: 1;

	${(props) =>
		!props.$isOpen &&
		css`
			&:hover {
				background-color: ${colorPalette.gray.c200Light};
				border-radius: ${radius.sm};
			}
		`}
`;

const AlertMessageStyled = styled.div`
	color: ${colorPalette.gray.c700Dark};
	font-size: ${fontSize.sm};
	line-height: ${baseDistance.md};
`;

const ButtonContainerStyled = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;

	.right-buttons {
		display: flex;
		gap: ${baseDistance.sm};
	}
`;
const AlertBodyStyled = styled.div<{$twoCols?: boolean}>`
	${ToggleSwitchFieldStyled} {
		padding-left: ${baseDistance.sm};
		height: 28px;
		label {
			width: 160px;
			font-size: ${fontSize.md};
			line-height: ${baseDistance.md};
			color: ${colorPalette.gray.c950};
		}
	}

	display: flex;
	flex: 1;
	flex-direction: column;
	gap: ${baseDistance.sm};

	.toggles {
		display: grid;
		grid-auto-rows: 24px;
		min-height: 16px;

		${({$twoCols}) =>
			$twoCols
				? `
          grid-template-rows: repeat(4, 24px); /* 4 rows per col */
          grid-auto-flow: column;
          grid-auto-columns: 162px;
        `
				: `
          grid-auto-flow: row;
          grid-template-columns: 1fr;
        `}
	}
`;

const AlertStyled = styled.div<{$isSpaceEditor: boolean; $isDetailsPanelOpen?: boolean}>`
	display: flex;
	border-radius: ${radius.md};
	box-shadow: 0px 4px 50px 0px #00000033;
	color: black;
	background-color: ${colorPalette.white};
	width: 352px;
	min-height: 160px;
	min-height: 136px;
	max-height: 208px;

	.IconButton {
		color: ${colorPalette.gray.c950};

		svg {
			height: 16px;
			width: 16px;
		}
	}

	.MultiActionButton {
		margin-left: auto;
	}

	.saveDropdown {
		width: auto !important;
		margin-left: auto !important;
		background-color: ${colorPalette.primary.c500Primary};
		color: ${colorPalette.white};
		font-size: ${fontSize.md};

		&:hover {
			background-color: ${colorPalette.primary.c700Dark};
		}
	}

	.yellow-strip {
		width: 4px;
		min-height: 136px;
		max-height: 208px;
		background-color: ${colorPalette.warning.c500Primary};
		border-radius: 8px 0 0 8px;
	}

	.body {
		display: flex;
		flex-direction: column;
		padding: ${baseDistance.sm};
		width: 100%;
		height: 100%;
	}
`;

const AlertPopupStyled = styled(PopupV5)`
	z-index: 10;

	${PopupBodyStyled} {
		padding: 0;
	}

	${PopupHeaderStyled} {
		display: none;
	}
`;

const SaveViewStyled = styled.div<{
	$isSpaceEditor: boolean;
	$isDetailsPanelOpen?: boolean;
	$dropDownStyle?: boolean;
	$isSideGrid?: boolean;
	$isBoundary?: boolean;
}>`
	position: fixed;
	right: ${(props) => (props.$isSpaceEditor ? "200px" : props.$isBoundary ? "16px" : "127px")};
	${(props) => props.$isSpaceEditor && "top: 87px;"}
	border: 1px solid ${colorPalette.warning.c500Primary};
	background-color: ${colorPalette.white};
	border-radius: ${radius.sm};
	height: 32px;
	min-width: 118px;
	align-items: center;
	display: flex;
	justify-content: space-between;
	z-index: 2;

	${(props) => {
		if (props.$isDetailsPanelOpen) {
			return css`
				position: absolute;
				top: 16px;
				z-index: 2;
			`;
		}
	}};

	${(props) => {
		if (props.$isSideGrid) {
			return css`
				position: absolute;
				right: ${props.$dropDownStyle ? "16px" : "200px"};
				top: ${props.$dropDownStyle ? "70px" : "24px"};
				z-index: 2;
			`;
		}
	}};

	.arrow-down {
		min-height: 30px;
		max-height: 30px;
		width: 30px;
		border-radius: 4px;

		svg {
			margin: 7px;
			height: 16px;
			width: 16px;
		}
	}

	.arrow-down:hover {
		background-color: ${colorPalette.gray.c200Light};
	}

	&:hover {
		cursor: pointer;
	}
`;
